import type { ReactNode } from "react";

import { cn, trimText } from "@/lib/utils";
import { formatNumber, mrpSkuHasSupplyChainNotes, type MrpSkuHoverHintFields } from "@/lib/mrp";
import { mrpSkuDisplayUnitLabel } from "@/lib/mrpSkuUnitDisplay";

const MRP_SKU_CATALOG_HOVER_HINT_SHELL_CLASS =
  "w-max max-w-[min(18rem,calc(100vw-2rem))] space-y-2 text-left";
const MRP_SKU_CATALOG_HOVER_HINT_DL_CLASS =
  "grid w-max max-w-full grid-cols-[auto_auto] gap-x-3 gap-y-1 text-[11px] leading-snug";

function hoverHintCatalogField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : "—";
}

/** Category / description / variant panel for SKU hover hints (forecast, batch details, and similar tables). */
export function MrpSkuCatalogHoverHintBody({
  sku,
  hint,
  loading = false,
}: {
  sku: string;
  hint: MrpSkuHoverHintFields | undefined;
  /** When true and `hint` is missing, show a blurred placeholder until catalog fields arrive. */
  loading?: boolean;
}): ReactNode {
  const showLoadingPlaceholder = loading && hint == null;
  const unitLabel = mrpSkuDisplayUnitLabel(hint?.mv_unit);

  return (
    <div className={MRP_SKU_CATALOG_HOVER_HINT_SHELL_CLASS}>
      <div className="border-b border-slate-100 pb-1.5 text-xs font-medium text-slate-800">
        <span className="font-mono">{sku}</span>
        {unitLabel != null ? (
          <span className="ml-1 font-sans font-medium text-slate-500">· {unitLabel}</span>
        ) : null}
      </div>
      <dl
        className={cn(
          MRP_SKU_CATALOG_HOVER_HINT_DL_CLASS,
          showLoadingPlaceholder && "blur-sm select-none opacity-50",
        )}
      >
        <dt className="text-slate-500">Category</dt>
        <dd className="max-w-[12rem] break-words text-right text-slate-800">
          {hoverHintCatalogField(hint?.category)}
        </dd>
        <dt className="text-slate-500">Description</dt>
        <dd className="max-w-[12rem] break-words text-right text-slate-800">
          {hoverHintCatalogField(hint?.description)}
        </dd>
        <dt className="text-slate-500">Variant</dt>
        <dd className="max-w-[12rem] break-words text-right text-slate-800">
          {hoverHintCatalogField(hint?.variant)}
        </dd>
        {unitLabel != null ? (
          <>
            <dt className="text-slate-500">Unit</dt>
            <dd className="max-w-[12rem] break-words text-right text-slate-800">{unitLabel}</dd>
          </>
        ) : null}
        {mrpSkuHasSupplyChainNotes(hint) ? (
          <>
            <dt className="col-span-2 pt-1 text-slate-500">Supply chain notes</dt>
            <dd className="col-span-2 max-w-[16rem] whitespace-pre-wrap break-words text-left text-slate-800">
              {trimText(hint?.supply_chain_notes)}
            </dd>
          </>
        ) : null}
      </dl>
      {showLoadingPlaceholder ? (
        <p className="text-[10px] text-slate-500">Loading catalog details…</p>
      ) : null}
    </div>
  );
}

/** Table of catalog fields (and optional quantities) for purchase order line list hover hints. */
export function MrpSkuCatalogHoverHintListBody({
  rows,
  hints,
}: {
  rows: readonly { sku: string; quantity_ordered: number; quantity_received: number | null }[];
  hints: Record<string, MrpSkuHoverHintFields> | undefined;
}): ReactNode {
  if (rows.length === 0) {
    return <span className="text-slate-500">No lines</span>;
  }
  return (
    <div className="max-w-[min(52rem,calc(100vw-2rem))] overflow-x-auto">
      <table className="min-w-full border-collapse text-left text-[11px] leading-snug">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            <th className="py-1 pr-3 font-medium">SKU</th>
            <th className="py-1 pr-3 font-medium">Category</th>
            <th className="py-1 pr-3 font-medium">Description</th>
            <th className="py-1 pr-3 font-medium">Variant</th>
            <th className="py-1 pr-3 text-right font-medium">Ordered</th>
            <th className="py-1 text-right font-medium">Received</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const hint = hints?.[row.sku];
            return (
              <tr key={`${row.sku}-${String(index)}`} className="border-b border-slate-100 last:border-none">
                <td className="py-1 pr-3 align-top font-mono text-slate-800">{row.sku}</td>
                <td className="py-1 pr-3 align-top text-slate-800">
                  {hoverHintCatalogField(hint?.category)}
                </td>
                <td className="max-w-[14rem] py-1 pr-3 align-top break-words text-slate-800">
                  {hoverHintCatalogField(hint?.description)}
                </td>
                <td className="max-w-[14rem] py-1 pr-3 align-top break-words text-slate-800">
                  {hoverHintCatalogField(hint?.variant)}
                </td>
                <td className="py-1 pr-3 align-top text-right font-mono tabular-nums text-slate-800">
                  {formatNumber(row.quantity_ordered)}
                </td>
                <td className="py-1 align-top text-right font-mono tabular-nums text-slate-800">
                  {formatNumber(row.quantity_received)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
